import type { TCreateSlice, TAsyncState } from "@/store";
import type { TApiAuth, TUser } from "@/apis/auth/apiAuth";
import type { TSliceLogin } from "../login/sliceLogin";
import type {
    TErrorGetInitialConfig,
    TPublicAuth0Config,
} from "@/apis/odin/apiODIN";
import { createAsyncSlice } from "@/store/createAsyncSlice";

export type { TUser } from "@/apis/auth/apiAuth";

type TStateAuth = {
    authUser: TUser | null;
    authInitState: TAsyncState<null, Error>;
    authSessionState: TAsyncState<null, Error>;
    authTokenState: TAsyncState<string | undefined, Error>;
    authRedirectState: TAsyncState<string | undefined, Error>;
    authLoginState: TAsyncState<null, Error>;
    authLogoutState: TAsyncState<null, Error>;
};

type TDerivedStateAuth = {
    isAuthLoading: (state: TStateAuth) => boolean;
    isAuthenticated: (state: TStateAuth) => boolean;
    isInitializingAuth: (state: TStateAuth) => boolean;
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    authError: (state: TStateAuth) => Error | TErrorGetInitialConfig | null;
};

type TActionsPrivateAuth = {
    setAuthUser: (user: TUser | null) => void;
    resetAuthState: () => void;
    login: (returnTo: string) => Promise<void>;
    checkIfUserIsAuthenticatedOrRedirectToLogin: (
        returnTo: string,
    ) => Promise<void>;
};

type TActionsPublicAuth = {
    logout: () => Promise<void>;
    checkIfUserIsAuthenticatedOrRedirectToLogin: (params: {
        returnTo: string;
    }) => Promise<void>;
    handleAuthRedirect: () => Promise<string | undefined>;
    getTokenOrRedirectToLogin: (params?: {
        returnTo: string;
    }) => Promise<string | undefined>;
    initializeAuth: (authConfig: TPublicAuth0Config) => Promise<void>;
};

export type TSliceAuth = TStateAuth & TActionsPublicAuth & TDerivedStateAuth;

export const initialStateAuth: TStateAuth = {
    authUser: null,
    authInitState: { status: "idle", data: null, error: null },
    authSessionState: { status: "idle", data: null, error: null },
    authTokenState: { status: "idle", data: null, error: null },
    authRedirectState: { status: "idle", data: null, error: null },
    authLoginState: { status: "idle", data: null, error: null },
    authLogoutState: { status: "idle", data: null, error: null },
};

export const createSliceAuth: TCreateSlice<
    TSliceAuth,
    {
        apiAuth: TApiAuth;
        sliceLogin: TSliceLogin;
    }
> =
    ({ apiAuth, sliceLogin }) =>
    (set, get) => {
        const { authInitState, runAuthInitState: initializeAuth } =
            createAsyncSlice<
                "authInitState",
                null,
                Error,
                [{ authConfig: TPublicAuth0Config }]
            >({
                name: "authInitState",
                asyncFn: async ({
                    authConfig,
                }: {
                    authConfig: TPublicAuth0Config;
                }) => {
                    await apiAuth.initialize(authConfig);
                    return null;
                },
            })(set);

        const { authLogoutState, runAuthLogoutState: logout } =
            createAsyncSlice<"authLogoutState", null, Error, []>({
                name: "authLogoutState",
                asyncFn: async () => {
                    await apiAuth.logout();
                    actionsPrivate.resetAuthState();
                    return null;
                },
                rethrowError: true,
            })(set);

        const { authRedirectState, runAuthRedirectState: handleAuthRedirect } =
            createAsyncSlice<
                "authRedirectState",
                string | undefined,
                Error,
                []
            >({
                name: "authRedirectState",
                asyncFn: async () => {
                    const returnTo = await apiAuth.handleRedirectCallback();
                    void sliceLogin.onLoginCallback({ returnTo });
                    const user = await apiAuth.getUser();
                    actionsPrivate.setAuthUser(user);
                    return returnTo;
                },
                rethrowError: true,
            })(set);

        const { authTokenState, runAuthTokenState: getTokenOrRedirectToLogin } =
            createAsyncSlice<
                "authTokenState",
                string | undefined,
                Error,
                [{ returnTo?: string }] | []
            >({
                name: "authTokenState",
                asyncFn: async (params?: { returnTo?: string }) => {
                    const { returnTo = "/" } = params ?? {};
                    try {
                        const token = await apiAuth.getTokenSilently();
                        return token;
                    } catch (error) {
                        const authError = error as { error: string };
                        if (
                            authError.error === "missing_refresh_token" ||
                            authError.error === "invalid_grant"
                        ) {
                            console.error(
                                "Token refresh failed, redirecting to login page...",
                            );
                            await actionsPrivate.login(returnTo);
                        } else {
                            throw error;
                        }
                        return undefined;
                    }
                },
            })(set);

        const { authSessionState, runAuthSessionState } = createAsyncSlice<
            "authSessionState",
            null,
            Error,
            [{ returnTo: string }]
        >({
            name: "authSessionState",
            asyncFn: async ({ returnTo }: { returnTo: string }) => {
                const token = await getTokenOrRedirectToLogin({
                    returnTo,
                });
                if (token === undefined) throw new Error("No token received");

                const user = await apiAuth.getUser();
                actionsPrivate.setAuthUser(user);
                return null;
            },
        })(set);

        const { authLoginState, runAuthLoginState: runAuthLogin } =
            createAsyncSlice<
                "authLoginState",
                null,
                Error,
                [{ returnTo: string }]
            >({
                name: "authLoginState",
                asyncFn: async ({ returnTo }: { returnTo: string }) => {
                    await apiAuth.loginWithRedirect(returnTo);
                    return null;
                },
            })(set);

        const derivedState: TDerivedStateAuth = {
            isAuthLoading: state => {
                const isInitializingAuth =
                    derivedState.isInitializingAuth(state);
                return (
                    isInitializingAuth ||
                    state.authRedirectState.status === "pending" ||
                    state.authLoginState.status === "pending" ||
                    state.authLogoutState.status === "pending" ||
                    state.authSessionState.status === "pending"
                );
            },
            isInitializingAuth: state =>
                state.authInitState.status === "idle" ||
                state.authInitState.status === "pending",
            isAuthenticated: state => !!state.authUser,
            authError: state =>
                state.authInitState.error ??
                state.authSessionState.error ??
                state.authTokenState.error ??
                state.authRedirectState.error ??
                state.authLoginState.error ??
                state.authLogoutState.error ??
                null,
        };

        const actionsPrivate: TActionsPrivateAuth = {
            setAuthUser: user => {
                set({ authUser: user }, false, "auth/setAuthUser");
            },
            resetAuthState: () => {
                set(initialStateAuth, false, "auth/reset");
            },
            login: async returnTo => {
                const loginState = get().authLoginState;
                const loginAlreadyInProgress = loginState.status === "pending";
                if (loginAlreadyInProgress) return;

                await runAuthLogin({ returnTo });
            },
            checkIfUserIsAuthenticatedOrRedirectToLogin: async (
                returnTo: string,
            ) => {
                const isAlreadyAuthenticated =
                    derivedState.isAuthenticated(get());
                if (isAlreadyAuthenticated) return;

                await runAuthSessionState({ returnTo });
            },
        };

        const actionsPublic: TActionsPublicAuth = {
            initializeAuth: async (authConfig: TPublicAuth0Config) => {
                await initializeAuth({ authConfig });
            },
            logout: async () => {
                await logout();
            },
            handleAuthRedirect,
            getTokenOrRedirectToLogin: (params?: { returnTo: string }) =>
                getTokenOrRedirectToLogin(params || {}),
            checkIfUserIsAuthenticatedOrRedirectToLogin: async (params: {
                returnTo: string;
            }) => {
                await actionsPrivate.checkIfUserIsAuthenticatedOrRedirectToLogin(
                    params.returnTo,
                );
            },
        };

        return {
            ...initialStateAuth,
            authInitState,
            authLoginState,
            authLogoutState,
            authRedirectState,
            authTokenState,
            authSessionState,
            ...actionsPublic,
            ...derivedState,
        };
    };
